import React, { useState, useMemo, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { WeddingInvitation } from '../types/invitation';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useCollaborationToast } from '../hooks/useCollaborationToast';
import useCollaborationPermissions from '../hooks/useCollaborationPermissions';
import { useBatchSelection } from '../hooks/useBatchSelection';
import { useSearch } from '../hooks/useSearch';
import { useCSVExport } from '../hooks/useCSVExport';
import { useRSVPData } from './rsvp/hooks/useRSVPData';
import { useGuestMutations } from './rsvp/hooks/useGuestMutations';
import { useBatchActions } from './rsvp/hooks/useBatchActions';
import { useTableAssignment } from './rsvp/hooks/useTableAssignment';
import { usePhysicalInvitations } from './rsvp/hooks/usePhysicalInvitations';
import { useRSVPExport } from './rsvp/hooks/useRSVPExport';
import { RSVPResponseWithTable } from './rsvp/types';
import ThemedCard from './themed/ThemedCard';
import CollaborationToast from './CollaborationToast';
import InvitationContextBanner from './InvitationContextBanner';
import { TabNavigation } from './rsvp/components/TabNavigation';
import { BatchActionBar } from './rsvp/components/BatchActionBar';
import { OverviewTab } from './rsvp/components/OverviewTab';
import { AttendeesTab } from './rsvp/components/AttendeesTab';
import { DeclinedTab } from './rsvp/components/DeclinedTab';
import { PhysicalInvitationsTab } from './rsvp/components/PhysicalInvitationsTab';
import { MessagesTab } from './rsvp/components/MessagesTab';
import { ExportOptionsModal } from './rsvp/components/ExportOptionsModal';
import { MainExportModal } from './rsvp/components/MainExportModal';
import { UpgradePrompt } from './UpgradePrompt';
import { PlanUsageBar } from './rsvp/components/PlanUsageBar';

interface RSVPTrackerProps {
  inviteCode: string;
  refreshTrigger?: number;
  onDataChange?: () => void;
  currentInvitation?: WeddingInvitation;
}

export const RSVPTracker: React.FC<RSVPTrackerProps> = ({
  inviteCode,
  refreshTrigger,
  onDataChange,
  currentInvitation
}) => {
  const { user } = useAuth();
  const { messages, showCollaborationAlert, dismissToast } = useCollaborationToast();
  const { canEdit, loading: permissionsLoading } = useCollaborationPermissions(currentInvitation?.id || null);
  const { limits, canAddGuest, canExport } = useSubscription();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  const [activeTab, setActiveTab] = useState<'overview' | 'attendees' | 'declined' | 'physical-invitations' | 'messages'>('overview');

  const {
    rsvpResponses,
    rsvpStats,
    physicalInvitationStats,
    loading,
    seatingData,
    attendees,
    declinedResponses,
    responsesWithMessages,
    seatAssignmentStats,
    confirmationRate,
    dietaryStats,
    refresh,
    setAttendees,
  } = useRSVPData(inviteCode, user?.id, refreshTrigger);

  const totalAttendingCount = useMemo(() => {
    return rsvpResponses
      .filter(r => r.rsvpData.attending === 'yes')
      .reduce((sum, r) => sum + (r.rsvpData.guestCount || 1), 0);
  }, [rsvpResponses]);

  const checkGuestLimit = useCallback((increment: number) => {
    if (!canAddGuest(currentInvitation, totalAttendingCount + increment - 1)) {
      setUpgradeMessage(`您的方案最多支援 ${limits.maxGuests} 位出席賓客，目前已有 ${totalAttendingCount} 位。升級方案以增加賓客數量。`);
      setShowUpgradePrompt(true);
      return false;
    }
    return true;
  }, [canAddGuest, currentInvitation, totalAttendingCount, limits]);

  const { searchTerm, setSearchTerm, filterBySearch } = useSearch();
  const { exportingData, setExportingData, downloadCSV } = useCSVExport();

  const {
    batchEditMode,
    selectedItems,
    batchDeleting,
    setBatchDeleting,
    toggleBatchEditMode,
    toggleSelectItem,
    toggleSelectAll,
    getSelectAllState,
    clearSelection,
  } = useBatchSelection();

  const {
    updatingGuestCount,
    editingNameId,
    editingNameValue,
    setEditingNameValue,
    updatingGuestName,
    deletingRSVPId,
    sendingNotificationId,
    checkGuestSeatingStatus,
    clearGuestFromSeating,
    handleUpdateGuestCount,
    handleGuestSideToggle,
    handleStartEditName,
    handleCancelEditName,
    handleUpdateGuestName,
    handleNameInputKeyDown,
    handleDeleteRSVP,
    handleSendIndividualNotification: rawSendNotification,
  } = useGuestMutations({
    inviteCode,
    userId: user?.id,
    rsvpResponses,
    onRefresh: refresh,
    onDataChange,
    showCollaborationAlert,
    checkGuestLimit,
  });

  const handleSendIndividualNotification = useCallback(async (attendee: RSVPResponseWithTable) => {
    await rawSendNotification(attendee);
    setAttendees(prev => prev.map(a =>
      a.id === attendee.id
        ? { ...a, notificationSent: true, notificationSentAt: new Date() }
        : a
    ));
  }, [rawSendNotification, setAttendees]);

  const { handleBatchDelete, handleBatchClearMessages } = useBatchActions({
    userId: user?.id,
    rsvpResponses,
    selectedItems,
    activeTab,
    onRefresh: refresh,
    onDataChange,
    clearSelection,
    setBatchDeleting,
    checkGuestSeatingStatus,
    clearGuestFromSeating,
  });

  const {
    editingTableId,
    setEditingTableId,
    handleUpdateTable,
    getAvailableTables,
  } = useTableAssignment({
    inviteCode,
    userId: user?.id,
    seatingData,
    onRefresh: refresh,
  });

  const {
    selectedPhysicalInvitations,
    copiedIndex,
    showExportOptions,
    setShowExportOptions,
    togglePhysicalInvitationSelection,
    selectAllPhysicalInvitations,
    getPhysicalInvitationSelectAllState,
    copyToClipboard,
    exportPhysicalInvitationsToCSV,
    handleExportSelectedMailingLabels,
  } = usePhysicalInvitations({
    rsvpResponses,
    physicalInvitationStats,
    downloadCSV,
    setExportingData,
  });

  const {
    showMainExportOptions,
    setShowMainExportOptions,
    selectedExportFields,
    setQuickExportPreset,
    toggleExportField,
    handleExportWithFields,
  } = useRSVPExport({
    inviteCode,
    userId: user?.id,
    downloadCSV,
    setExportingData,
  });

  const filteredAttendees = useMemo(() => filterBySearch(attendees), [filterBySearch, attendees]);
  const filteredDeclined = useMemo(() => filterBySearch(declinedResponses), [filterBySearch, declinedResponses]);
  const filteredMessages = useMemo(() => {
    if (!searchTerm.trim()) return responsesWithMessages;
    const term = searchTerm.toLowerCase();
    return responsesWithMessages.filter(r =>
      r.guestName.toLowerCase().includes(term) ||
      r.guestEmail.toLowerCase().includes(term) ||
      (r.rsvpData.message && r.rsvpData.message.toLowerCase().includes(term)) ||
      (r.rsvpData.specialRequests && r.rsvpData.specialRequests.toLowerCase().includes(term))
    );
  }, [responsesWithMessages, searchTerm]);

  const wrapToggleSelectAll = useCallback((items: RSVPResponseWithTable[]) => {
    toggleSelectAll(items.map(i => i.id));
  }, [toggleSelectAll]);

  const wrapGetSelectAllState = useCallback((items: RSVPResponseWithTable[]) => {
    return getSelectAllState(items.map(i => i.id));
  }, [getSelectAllState]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">載入 RSVP 資料中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InvitationContextBanner invitation={currentInvitation || null} />

      {currentInvitation?.role !== 'collaborator' && limits.maxGuests !== null && (
        <PlanUsageBar
          current={totalAttendingCount}
          max={limits.maxGuests}
          label="出席賓客用量"
        />
      )}

      {permissionsLoading && (
        <div className="text-center py-4">
          <div className="text-wedding-text-body">檢查權限中...</div>
        </div>
      )}

      {!permissionsLoading && !canEdit && (
        <ThemedCard className="p-6 text-center">
          <div className="text-wedding-text-body">
            您沒有權限管理此邀請函的 RSVP 資料。請聯絡邀請函擁有者。
          </div>
        </ThemedCard>
      )}

      <TabNavigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        attendeesCount={attendees.length}
        declinedCount={declinedResponses.length}
        physicalInvitationStats={physicalInvitationStats}
        messagesCount={responsesWithMessages.length}
        batchEditMode={batchEditMode}
        toggleBatchEditMode={toggleBatchEditMode}
        loading={loading}
        exportingData={exportingData}
        onRefresh={refresh}
        onShowMainExport={() => {
          if (!canExport(currentInvitation)) {
            setUpgradeMessage('匯出功能需要標準方案或以上。升級方案以使用匯出功能。');
            setShowUpgradePrompt(true);
            return;
          }
          setShowMainExportOptions(true);
        }}
      />

      {batchEditMode && (
        <BatchActionBar
          selectedCount={selectedItems.size}
          batchDeleting={batchDeleting}
          activeTab={activeTab}
          onBatchDelete={handleBatchDelete}
          onBatchClearMessages={handleBatchClearMessages}
          onCancel={clearSelection}
        />
      )}

      {canEdit && (
        <>
          {activeTab === 'overview' && (
            <OverviewTab
              rsvpStats={rsvpStats}
              confirmationRate={confirmationRate}
              physicalInvitationStats={physicalInvitationStats}
              seatAssignmentStats={seatAssignmentStats}
              rsvpResponses={rsvpResponses}
              dietaryStats={dietaryStats}
            />
          )}

          {activeTab === 'attendees' && (
            <AttendeesTab
              filteredAttendees={filteredAttendees}
              maxGuests={currentInvitation?.role === 'collaborator' ? null : limits.maxGuests}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              batchEditMode={batchEditMode}
              selectedItems={selectedItems}
              toggleSelectAll={wrapToggleSelectAll}
              getSelectAllState={wrapGetSelectAllState}
              toggleSelectItem={toggleSelectItem}
              editingNameId={editingNameId}
              editingNameValue={editingNameValue}
              setEditingNameValue={setEditingNameValue}
              updatingGuestName={updatingGuestName}
              handleStartEditName={handleStartEditName}
              handleCancelEditName={handleCancelEditName}
              handleUpdateGuestName={handleUpdateGuestName}
              handleNameInputKeyDown={handleNameInputKeyDown}
              updatingGuestCount={updatingGuestCount}
              handleUpdateGuestCount={handleUpdateGuestCount}
              editingTableId={editingTableId}
              setEditingTableId={setEditingTableId}
              handleUpdateTable={handleUpdateTable}
              getAvailableTables={getAvailableTables}
              handleGuestSideToggle={handleGuestSideToggle}
              sendingNotificationId={sendingNotificationId}
              handleSendIndividualNotification={handleSendIndividualNotification}
              deletingRSVPId={deletingRSVPId}
              handleDeleteRSVP={handleDeleteRSVP}
            />
          )}

          {activeTab === 'declined' && (
            <DeclinedTab
              filteredDeclined={filteredDeclined}
              searchTerm={searchTerm}
              batchEditMode={batchEditMode}
              selectedItems={selectedItems}
              toggleSelectAll={wrapToggleSelectAll}
              getSelectAllState={wrapGetSelectAllState}
              toggleSelectItem={toggleSelectItem}
              handleDeleteRSVP={handleDeleteRSVP}
              deletingRSVPId={deletingRSVPId}
            />
          )}

          {activeTab === 'physical-invitations' && (
            <PhysicalInvitationsTab
              physicalInvitationStats={physicalInvitationStats}
              rsvpResponses={rsvpResponses}
              exportingData={exportingData}
              selectedPhysicalInvitations={selectedPhysicalInvitations}
              copiedIndex={copiedIndex}
              togglePhysicalInvitationSelection={togglePhysicalInvitationSelection}
              selectAllPhysicalInvitations={selectAllPhysicalInvitations}
              getPhysicalInvitationSelectAllState={getPhysicalInvitationSelectAllState}
              copyToClipboard={copyToClipboard}
              exportPhysicalInvitationsToCSV={(includePhone?: boolean) => {
                if (!canExport(currentInvitation)) {
                  setUpgradeMessage('匯出功能需要標準方案或以上。升級方案以使用匯出功能。');
                  setShowUpgradePrompt(true);
                  return;
                }
                exportPhysicalInvitationsToCSV(includePhone);
              }}
              setShowExportOptions={(v: boolean) => {
                if (v && !canExport(currentInvitation)) {
                  setUpgradeMessage('匯出功能需要標準方案或以上。升級方案以使用匯出功能。');
                  setShowUpgradePrompt(true);
                  return;
                }
                setShowExportOptions(v);
              }}
            />
          )}

          {activeTab === 'messages' && (
            <MessagesTab
              filteredMessages={filteredMessages}
              searchTerm={searchTerm}
              batchEditMode={batchEditMode}
              selectedItems={selectedItems}
              toggleSelectAll={wrapToggleSelectAll}
              getSelectAllState={wrapGetSelectAllState}
              toggleSelectItem={toggleSelectItem}
              handleDeleteRSVP={handleDeleteRSVP}
              deletingRSVPId={deletingRSVPId}
            />
          )}

          {showExportOptions && (
            <ExportOptionsModal
              selectedCount={selectedPhysicalInvitations.size}
              onExport={handleExportSelectedMailingLabels}
              onClose={() => setShowExportOptions(false)}
            />
          )}

          {showMainExportOptions && (
            <MainExportModal
              rsvpStats={rsvpStats}
              exportingData={exportingData}
              selectedExportFields={selectedExportFields}
              onSetQuickPreset={setQuickExportPreset}
              onToggleField={toggleExportField}
              onResetFields={() => setShowMainExportOptions(false)}
              onExport={handleExportWithFields}
              onClose={() => setShowMainExportOptions(false)}
            />
          )}
        </>
      )}

      <CollaborationToast
        messages={messages}
        onDismiss={dismissToast}
      />

      <UpgradePrompt
        open={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        message={upgradeMessage}
      />
    </div>
  );
};
